import { create } from 'zustand'

import { FlowMetadata } from '../core/types/chat.types'

export type ExploreSelection = {
  condition: 'month_and_year' | 'year1_and_year2'
  monthChosen?: number
  yearChosen?: number
  year1?: number
  year2?: number
}

type MaxFlowStoreState = {
  flowMetadataByConversation: Record<string, FlowMetadata>
  exploreSelectionsByConversation: Record<string, ExploreSelection>
  budgetAnswersByConversation: Record<string, Record<string, string>>
  budgetReferenceMessageIdByConversation: Record<string, string>
  compareReferenceMessageIdByConversation: Record<string, string>
}

type MaxFlowStoreActions = {
  getFlowMetadata: (conversationId: string | null | undefined) => FlowMetadata | null
  setFlowMetadata: (
    conversationId: string | null | undefined,
    metadata: FlowMetadata | null | undefined
  ) => void
  clearAll: () => void
  isAwaitingUpload: (conversationId: string | null | undefined) => boolean
  setExploreSelections: (
    conversationId: string | null | undefined,
    selection: ExploreSelection | null
  ) => void
  getExploreSelections: (conversationId: string | null | undefined) => ExploreSelection | null
  getBudgetAnswers: (conversationId: string | null | undefined) => Record<string, string>
  setBudgetAnswers: (
    conversationId: string | null | undefined,
    answers: Record<string, string>
  ) => void
  setBudgetAnswer: (
    conversationId: string | null | undefined,
    stepKey: string,
    value: string
  ) => void
  clearBudgetAnswers: (conversationId: string | null | undefined) => void
  getBudgetReferenceMessageId: (conversationId: string | null | undefined) => string | null
  setBudgetReferenceMessageId: (
    conversationId: string | null | undefined,
    messageId: string
  ) => void
  clearBudgetReferenceMessageId: (conversationId: string | null | undefined) => void
  clearBudgetState: (conversationId: string | null | undefined) => void
  getCompareReferenceMessageId: (conversationId: string | null | undefined) => string | null
  setCompareReferenceMessageId: (
    conversationId: string | null | undefined,
    messageId: string
  ) => void
  clearCompareReferenceMessageId: (conversationId: string | null | undefined) => void
  migrateConversationState: (
    tempConversationId: string | null | undefined,
    realConversationId: string | null | undefined
  ) => void
}

type MaxFlowStore = MaxFlowStoreState & MaxFlowStoreActions

const useMaxFlowStore = create<MaxFlowStore>((set, get) => ({
  flowMetadataByConversation: {},
  exploreSelectionsByConversation: {},
  budgetAnswersByConversation: {},
  budgetReferenceMessageIdByConversation: {},
  compareReferenceMessageIdByConversation: {},

  getFlowMetadata: (conversationId) => {
    if (!conversationId) return null
    return get().flowMetadataByConversation[conversationId] || null
  },

  setFlowMetadata: (conversationId, metadata) => {
    if (!conversationId) return

    set((state) => {
      if (!metadata) {
        const next = { ...state.flowMetadataByConversation }
        delete next[conversationId]
        return {
          flowMetadataByConversation: next,
        }
      }

      return {
        flowMetadataByConversation: {
          ...state.flowMetadataByConversation,
          [conversationId]: metadata,
        },
      }
    })
  },

  clearAll: () =>
    set({
      flowMetadataByConversation: {},
      exploreSelectionsByConversation: {},
      budgetAnswersByConversation: {},
      budgetReferenceMessageIdByConversation: {},
      compareReferenceMessageIdByConversation: {},
    }),

  isAwaitingUpload: (conversationId) => {
    const metadata = get().getFlowMetadata(conversationId)
    const flow = metadata?.flow
    // Only returns true for COMPARE flow upload steps, not any arbitrary upload state.
    if (typeof flow !== 'string' || flow.toUpperCase() !== 'COMPARE') return false
    if (!metadata?.step) return false
    const step = metadata.step.toLowerCase()
    return step === 'awaiting_upload' || step === 'upload' || step.includes('upload')
  },

  setExploreSelections: (conversationId, selection) => {
    if (!conversationId) return

    set((state) => {
      if (!selection) {
        const next = { ...state.exploreSelectionsByConversation }
        delete next[conversationId]
        return {
          exploreSelectionsByConversation: next,
        }
      }

      return {
        exploreSelectionsByConversation: {
          ...state.exploreSelectionsByConversation,
          [conversationId]: selection,
        },
      }
    })
  },

  getExploreSelections: (conversationId) => {
    if (!conversationId) return null
    return get().exploreSelectionsByConversation[conversationId] || null
  },

  getBudgetAnswers: (conversationId) => {
    if (!conversationId) return {}
    return get().budgetAnswersByConversation[conversationId] || {}
  },

  setBudgetAnswers: (conversationId, answers) => {
    if (!conversationId) return

    set((state) => ({
      budgetAnswersByConversation: {
        ...state.budgetAnswersByConversation,
        [conversationId]: answers,
      },
    }))
  },

  setBudgetAnswer: (conversationId, stepKey, value) => {
    if (!conversationId) return

    set((state) => ({
      budgetAnswersByConversation: {
        ...state.budgetAnswersByConversation,
        [conversationId]: {
          ...(state.budgetAnswersByConversation[conversationId] || {}),
          [stepKey]: value,
        },
      },
    }))
  },

  clearBudgetAnswers: (conversationId) => {
    if (!conversationId) return

    set((state) => {
      const next = { ...state.budgetAnswersByConversation }
      delete next[conversationId]
      return {
        budgetAnswersByConversation: next,
      }
    })
  },

  getBudgetReferenceMessageId: (conversationId) => {
    if (!conversationId) return null
    return get().budgetReferenceMessageIdByConversation[conversationId] || null
  },

  setBudgetReferenceMessageId: (conversationId, messageId) => {
    if (!conversationId) return

    set((state) => ({
      budgetReferenceMessageIdByConversation: {
        ...state.budgetReferenceMessageIdByConversation,
        [conversationId]: messageId,
      },
    }))
  },

  clearBudgetReferenceMessageId: (conversationId) => {
    if (!conversationId) return

    set((state) => {
      const next = { ...state.budgetReferenceMessageIdByConversation }
      delete next[conversationId]
      return {
        budgetReferenceMessageIdByConversation: next,
      }
    })
  },

  clearBudgetState: (conversationId) => {
    get().clearBudgetAnswers(conversationId)
    get().clearBudgetReferenceMessageId(conversationId)
  },

  getCompareReferenceMessageId: (conversationId) => {
    if (!conversationId) return null
    return get().compareReferenceMessageIdByConversation[conversationId] || null
  },

  setCompareReferenceMessageId: (conversationId, messageId) => {
    if (!conversationId) return

    set((state) => ({
      compareReferenceMessageIdByConversation: {
        ...state.compareReferenceMessageIdByConversation,
        [conversationId]: messageId,
      },
    }))
  },

  clearCompareReferenceMessageId: (conversationId) => {
    if (!conversationId) return

    set((state) => {
      const next = { ...state.compareReferenceMessageIdByConversation }
      delete next[conversationId]
      return {
        compareReferenceMessageIdByConversation: next,
      }
    })
  },

  migrateConversationState: (tempConversationId, realConversationId) => {
    if (!tempConversationId || !realConversationId || tempConversationId === realConversationId) {
      return
    }

    set((state) => {
      const nextFlowMetadata = { ...state.flowMetadataByConversation }
      const nextExploreSelections = { ...state.exploreSelectionsByConversation }
      const nextBudgetAnswers = { ...state.budgetAnswersByConversation }
      const nextBudgetReferences = { ...state.budgetReferenceMessageIdByConversation }
      const nextCompareReferences = { ...state.compareReferenceMessageIdByConversation }

      if (state.flowMetadataByConversation[tempConversationId]) {
        nextFlowMetadata[realConversationId] = state.flowMetadataByConversation[tempConversationId]
        delete nextFlowMetadata[tempConversationId]
      }

      if (state.exploreSelectionsByConversation[tempConversationId]) {
        nextExploreSelections[realConversationId] =
          state.exploreSelectionsByConversation[tempConversationId]
        delete nextExploreSelections[tempConversationId]
      }

      if (state.budgetAnswersByConversation[tempConversationId]) {
        nextBudgetAnswers[realConversationId] = state.budgetAnswersByConversation[tempConversationId]
        delete nextBudgetAnswers[tempConversationId]
      }

      if (state.budgetReferenceMessageIdByConversation[tempConversationId]) {
        nextBudgetReferences[realConversationId] =
          state.budgetReferenceMessageIdByConversation[tempConversationId]
        delete nextBudgetReferences[tempConversationId]
      }

      if (state.compareReferenceMessageIdByConversation[tempConversationId]) {
        nextCompareReferences[realConversationId] =
          state.compareReferenceMessageIdByConversation[tempConversationId]
        delete nextCompareReferences[tempConversationId]
      }

      return {
        flowMetadataByConversation: nextFlowMetadata,
        exploreSelectionsByConversation: nextExploreSelections,
        budgetAnswersByConversation: nextBudgetAnswers,
        budgetReferenceMessageIdByConversation: nextBudgetReferences,
        compareReferenceMessageIdByConversation: nextCompareReferences,
      }
    })
  },
}))

export default useMaxFlowStore
